import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StatusChip } from '@/components/ui/status-chip';
import { cn } from '@/lib/utils';
import { differenceInCalendarDays } from 'date-fns';
import { formatDate } from '@/lib/formatDate';
import { getAdmissions, type AdmissionRow } from '@/lib/ipdApi';
import { usePermission } from '@/hooks/usePermission';
import { admissionStatus, bedLabel } from '@/components/IPD/admissionStatus';
import AdmitPatientModal from '@/components/IPD/AdmitPatientModal';

// Same count as the IPD list's side panel: nights between admission and discharge (or today)
function stayLength(a: AdmissionRow): string {
  if (!a.admission_date) return '—';
  const days = differenceInCalendarDays(a.discharge_date ? new Date(a.discharge_date) : new Date(), new Date(a.admission_date));
  if (days <= 0) return a.discharge_date ? 'Same day' : 'Admitted today';
  return `${days} day${days === 1 ? '' : 's'}`;
}

const rupees = (n: number | null | undefined) => `₹${Number(n ?? 0).toLocaleString('en-IN')}`;

// One grid for header and rows from xl up so columns line up; below xl each admission stacks
const ROW_GRID = 'xl:grid xl:grid-cols-[9.5rem_minmax(0,1.2fr)_minmax(0,1fr)_13.5rem_6.5rem_7rem_7rem] xl:gap-3 xl:items-center';

interface PatientIPDTabProps {
  patientId: string;
  patient: { name: string; age?: number; gender?: string; phone?: string; mrn?: string };
  clinicId: string;
  canWrite: boolean;
}

/** Patient page: where the patient is admitted now, and every earlier admission. A row opens the admission. */
export default function PatientIPDTab({ patientId, patient, clinicId, canWrite }: PatientIPDTabProps) {
  const navigate = useNavigate();
  const { can } = usePermission();
  const showMoney = can('billing:read');
  const [admissions, setAdmissions] = useState<AdmissionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [admitOpen, setAdmitOpen] = useState(false);

  const load = useCallback(() => {
    if (!clinicId) return;
    setLoading(true);
    getAdmissions(clinicId, { status: 'all', patientId })
      .then(data => setAdmissions(data ?? []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [clinicId, patientId]);

  useEffect(() => { load(); }, [load]);

  const activeAdmission = admissions.find(a => a.status === 'admitted');
  const open = (a: AdmissionRow) => navigate(`/ipd/admissions/${a.id}`, { state: { from: `/patients/${patientId}` } });
  const dueOf = (a: AdmissionRow) => (a.total_amount != null ? Number(a.balance_due ?? 0) : 0);

  const admitButton = canWrite && !activeAdmission && (
    <Button size="sm" onClick={() => setAdmitOpen(true)} className="flex-shrink-0 gap-1.5">
      <Plus className="w-4 h-4" /> Admit to IPD
    </Button>
  );

  return (
    <div className="space-y-5">
      {/* Admitted now: where, since when, and what is owed — with the way into the admission */}
      {activeAdmission && (
        <section aria-label="Current admission" className="rounded-lg border border-border bg-card overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 min-h-11 px-4 py-2 border-b border-border">
            <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
              Currently admitted
              <StatusChip tone="accent">{stayLength(activeAdmission)}</StatusChip>
            </h2>
            <Button size="sm" variant="outline" onClick={() => open(activeAdmission)} className="gap-1.5">
              Open admission <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </div>
          <dl className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border">
            <div className="bg-card px-4 py-3 min-w-0">
              <dt className="text-xs text-muted-foreground">Ward / bed</dt>
              <dd className="mt-0.5 text-sm font-medium text-foreground truncate">{activeAdmission.ward_name} · {bedLabel(activeAdmission.bed_number)}</dd>
            </div>
            <div className="bg-card px-4 py-3 min-w-0">
              <dt className="text-xs text-muted-foreground">Admitted</dt>
              <dd className="mt-0.5 text-sm font-medium text-foreground tabular-nums">
                {formatDate(activeAdmission.admission_date)}
                <span className="block truncate font-mono font-normal text-xs text-muted-foreground">{activeAdmission.admission_number}</span>
              </dd>
            </div>
            <div className="bg-card px-4 py-3 min-w-0">
              <dt className="text-xs text-muted-foreground">Doctor</dt>
              <dd className="mt-0.5 text-sm font-medium text-foreground truncate">{activeAdmission.admitting_doctor_name || '—'}</dd>
            </div>
            <div className="bg-card px-4 py-3 min-w-0">
              <dt className="text-xs text-muted-foreground">Bill</dt>
              <dd className="mt-0.5 text-sm">
                {!showMoney || activeAdmission.total_amount == null ? (
                  <span className="text-muted-foreground">{showMoney ? 'No bill yet' : '—'}</span>
                ) : (
                  <>
                    <span className="font-mono tabular-nums font-medium text-foreground">{rupees(activeAdmission.total_amount)}</span>
                    {dueOf(activeAdmission) > 0 && (
                      <span className="ml-2 rounded bg-highlight px-1.5 py-0.5 font-mono text-xs font-semibold tabular-nums text-highlight-foreground">
                        {rupees(dueOf(activeAdmission))} due
                      </span>
                    )}
                  </>
                )}
              </dd>
            </div>
          </dl>
        </section>
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
      ) : admissions.length === 0 ? (
        <div className="rounded-lg border border-border bg-card px-6 py-12 text-center">
          <p className="text-sm font-semibold text-foreground">No admissions</p>
          <p className="text-sm text-muted-foreground mt-1">{patient.name} has not been admitted to IPD.</p>
          {admitButton && <div className="mt-4 flex justify-center">{admitButton}</div>}
        </div>
      ) : (
        <section aria-label="Admissions" className="space-y-3">
          <div className="flex items-center justify-between gap-3 min-h-9">
            <h2 className="text-sm font-semibold text-foreground">
              Admissions <span className="ml-1 font-normal text-muted-foreground tabular-nums">{admissions.length}</span>
            </h2>
            {admitButton}
          </div>

          <div role="table" aria-label="Admissions" className="rounded-lg border border-border bg-card overflow-hidden">
            <div role="row" className={cn(ROW_GRID, 'hidden h-8 px-4 border-b border-border bg-muted/40 text-2xs font-medium uppercase tracking-wide text-muted-foreground')}>
              <span role="columnheader">Adm. no.</span>
              <span role="columnheader">Ward / bed</span>
              <span role="columnheader">Doctor</span>
              <span role="columnheader">Admitted – discharged</span>
              <span role="columnheader">Stay</span>
              <span role="columnheader">Status</span>
              <span role="columnheader" className="text-right">{showMoney ? 'Due' : ''}</span>
            </div>
            {admissions.map(a => {
              const status = admissionStatus(a.status);
              const due = dueOf(a);
              return (
                <div
                  key={a.id}
                  role="row"
                  tabIndex={0}
                  onClick={() => open(a)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(a); } }}
                  className={cn(
                    ROW_GRID,
                    'flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 xl:py-0 xl:h-10 border-b border-divider last:border-b-0 cursor-pointer transition-colors',
                    'hover:bg-muted/50 focus-visible:outline-none focus-visible:bg-muted/60',
                  )}
                >
                  <span role="cell" className="font-mono text-sm text-foreground">{a.admission_number}</span>
                  <span role="cell" className="text-sm font-medium text-foreground truncate">{a.ward_name} · {bedLabel(a.bed_number)}</span>
                  <span role="cell" className="basis-full xl:basis-auto text-sm text-muted-foreground truncate">{a.admitting_doctor_name || '—'}</span>
                  <span role="cell" className="text-[13px] text-muted-foreground tabular-nums whitespace-nowrap">
                    {a.admission_date ? formatDate(a.admission_date) : '—'} – {a.discharge_date ? formatDate(a.discharge_date) : 'present'}
                  </span>
                  <span role="cell" className="text-[13px] text-muted-foreground tabular-nums whitespace-nowrap">{stayLength(a)}</span>
                  <span role="cell"><StatusChip tone={status.tone}>{status.label}</StatusChip></span>
                  <span role="cell" className={cn('ml-auto xl:ml-0 text-right text-sm font-mono tabular-nums', showMoney && due > 0 ? 'font-medium text-highlight-foreground' : 'text-muted-foreground')}>
                    {showMoney && due > 0 ? <><span className="xl:hidden mr-1 font-sans text-2xs text-muted-foreground">Due</span>{rupees(due)}</> : <span className="hidden xl:inline">{showMoney ? '—' : ''}</span>}
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Admit Patient Modal */}
      {clinicId && (
        <AdmitPatientModal
          open={admitOpen}
          onClose={() => setAdmitOpen(false)}
          clinicId={clinicId}
          initialPatient={{
            id: patientId,
            name: patient.name,
            age: patient.age,
            gender: patient.gender,
            phone: patient.phone,
            mrn: patient.mrn,
          }}
          onAdmitted={id => {
            setAdmitOpen(false);
            navigate(`/ipd/admissions/${id}`, { state: { from: `/patients/${patientId}` } });
          }}
        />
      )}
    </div>
  );
}
